import { Flags, FlagsProvider, FlagValue } from "@toolprint/sdk";

import { ConfigProvider } from "./config/provider.js";
import { AuthProvider } from "./auth/provider.js";
import {
  AnalyticsProvider,
  AnalyticsProviderImpl,
  DummyAnalyticsProvider,
} from "./analytics/provider.js";

import { getAppConfig } from "./app.js";
import { logger } from "./logger.js";
import { newSecretStorage } from "./config/secrets.js";
import { storedConfigSchema } from "./config/schemas.js";

export type {
  ConfigProvider,
  AuthProvider,
  AnalyticsProvider,
  DummyAnalyticsProvider,
};

export interface Providers {
  readonly configProvider: ConfigProvider;
  readonly authProvider: AuthProvider;
  readonly analyticsProvider: AnalyticsProviderImpl;
  readonly flagsProvider: ValidatedFlagsProvider;
}

/**
 * The flags that are specific to the CLI.
 */
const CLI_FLAGS = ["onegrep-cli-main-menu-settings-enabled"];

class FlagConfigurationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FlagConfigurationError";
  }
}

class ValidatedFlagsProvider {
  private readonly configProvider: ConfigProvider;
  private readonly authProvider: AuthProvider;

  constructor(configProvider: ConfigProvider, authProvider: AuthProvider) {
    this.configProvider = configProvider;
    this.authProvider = authProvider;
  }

  /**
   * Lazy-initializes the flags provider because we might not have
   * authentication configured at the time of instantiation.
   */
  private async getProvider(): Promise<FlagsProvider> {
    return new FlagsProvider(await this.authProvider.getApi());
  }

  private isValidFlagName(flagName: string): boolean {
    return CLI_FLAGS.includes(flagName);
  }

  validateFlagName(flagName: string): void {
    if (!this.isValidFlagName(flagName)) {
      throw new FlagConfigurationError(
        `Flag ${flagName} is not a valid CLI flag`,
      );
    }
  }

  async all(): Promise<Flags> {
    const flags = await (await this.getProvider()).all();
    // Filter flags to only include known CLI-specific flags
    const cliFlags: Flags = {};
    for (const flagName of CLI_FLAGS) {
      if (this.isValidFlagName(flagName)) {
        cliFlags[flagName] = flags[flagName];
      }
    }
    return cliFlags;
  }

  async value(flagName: string): Promise<FlagValue | undefined> {
    this.validateFlagName(flagName);
    return (await this.getProvider()).value(flagName);
  }
}

export async function getProviders(): Promise<Providers> {
  try {
    const appConfig = await getAppConfig();

    const configDirSecretStorage = await newSecretStorage(
      storedConfigSchema,
      "config-dir",
    );
    const configProvider = new ConfigProvider(
      appConfig,
      configDirSecretStorage,
    );
    await configProvider.init();

    const authProvider = new AuthProvider({ configProvider });

    let analyticsProvider: AnalyticsProviderImpl;
    if (!!appConfig.posthog?.apiKey) {
      analyticsProvider = new AnalyticsProvider({
        appConfig,
        configProvider,
      });
    } else {
      logger.debug("No PostHog API key found, using DummyAnalyticsProvider");
      analyticsProvider = new DummyAnalyticsProvider({
        appConfig,
        configProvider,
      });
    }

    return {
      configProvider: configProvider,
      authProvider: authProvider,
      analyticsProvider: analyticsProvider,
      flagsProvider: new ValidatedFlagsProvider(configProvider, authProvider),
    };
  } catch (error) {
    logger.error(`Error getting providers: ${error}`);
    throw error;
  }
}
